import HomeIconInactive from "./images/icons/homeIconInactive.svg?react";
import HomeIconActive from "./images/icons/homeIconActive.svg?react";
import ExploreIconActive from "./images/icons/exploreIconActive.svg?react";
import ExploreIconInactive from "./images/icons/exploreIconInactive.svg?react";
import CategoryIconInactive from "./images/icons/categoryIcon.svg?react";
import CategoryIconActive from "./images/icons/categoryActiveIcon.svg?react";
import PlusIcon from "./images/icons/plusThink.svg?react";
import PlusThinkActive from "./images/icons/plusThinkActive.svg?react";
import BookmarkIconActive from "./images/icons/bookmarkIconActive.svg?react";
import BookmarkIconInactive from "@/common/images/icons/saved.svg?react";
import ProfileIconActive from "./images/icons/profileIconActive.svg?react";
import ProfileIconInactive from "./images/icons/profileIconInactive.svg?react";
import StackIcon from "@/common/images/icons/stack.svg?react";
import StackIconActive from "@/common/images/icons/stack-filled.svg?react";
import { clsx } from "clsx";
import { TabLink } from "../TabLink";
import { routes } from "@/common/utils/routes";
import { TABS_LABELS, TABS_URLS } from "./helpers";
import { useAppStore } from "@/common/store/appStore";
import { useExperiment } from "@/common/hooks/useExperiment";
import { EXPERIMENT_NAVBAR_NICHE_BUTTON, VARIANT_TREATMENT } from "@/common/services/experiment/constants";
import { useMemo } from "react";
import "./ApplicationFooter.scss";

interface IApplicationFooterProps {
  currentUserName: string | null;
  onSendMetricAndCloseMenu: (tabName: TABS_URLS, label: TABS_LABELS, targetUrl: string) => void;
  isTabActive: (tab: string) => string | boolean | null;
  onProfileClick: () => void;
}

export const ApplicationFooter: React.FC<IApplicationFooterProps> = ({
  currentUserName,
  onSendMetricAndCloseMenu,
  isTabActive,
  onProfileClick,
}) => {
  const { variant } = useExperiment({ key: EXPERIMENT_NAVBAR_NICHE_BUTTON, fallback: false });
  const isProfileMenuOpen = useAppStore((state) => state.isProfileMenuOpen);

  const footerTabs = useMemo(() => {
    const isNicheVariant = variant === VARIANT_TREATMENT;

    return [
      {
        to: routes.home(),
        type: TABS_URLS.home,
        label: TABS_LABELS.home,
        alwaysShow: true,
        onClick: () => onSendMetricAndCloseMenu(TABS_URLS.home, TABS_LABELS.home, routes.home()),
        icon: isTabActive(TABS_URLS.home) ? <HomeIconActive /> : <HomeIconInactive />
      },
      {
        to: routes.explore(),
        type: TABS_URLS.explore,
        label: TABS_LABELS.explore,
        alwaysShow: true,
        onClick: () => onSendMetricAndCloseMenu(TABS_URLS.explore, TABS_LABELS.explore, routes.explore()),
        icon: isTabActive(TABS_URLS.explore) ? <ExploreIconActive /> : <ExploreIconInactive />
      },
      {
        to: routes.create(),
        type: TABS_URLS.add,
        label: TABS_LABELS.add,
        alwaysShow: true,
        onClick: () => onSendMetricAndCloseMenu(TABS_URLS.add, TABS_LABELS.add, routes.create()),
        icon: isTabActive(TABS_URLS.add) ? <PlusThinkActive /> : <PlusIcon />
      },
      {
        to: routes.userCollections(currentUserName || ""),
        type: TABS_URLS.saved,
        label: TABS_LABELS.saved,
        alwaysShow: false,
        condition: !!currentUserName,
        onClick: () => onSendMetricAndCloseMenu(TABS_URLS.saved, TABS_LABELS.saved, routes.userCollections(currentUserName || "")),
        icon: isTabActive(TABS_URLS.saved) ? <BookmarkIconActive /> : <BookmarkIconInactive />
      },
      ...(isNicheVariant ? [{
        to: routes.niches(),
        type: TABS_URLS.niches,
        label: TABS_LABELS.niches,
        alwaysShow: false,
        condition: !currentUserName,
        onClick: () => onSendMetricAndCloseMenu(TABS_URLS.niches, TABS_LABELS.niches, routes.niches()),
        icon: isTabActive(TABS_URLS.niches) ? <StackIconActive /> : <StackIcon />
      }] : [{
        to: routes.trendingTags(),
        type: TABS_URLS.categories,
        label: TABS_LABELS.categories,
        alwaysShow: false,
        condition: !currentUserName,
        onClick: () => onSendMetricAndCloseMenu(TABS_URLS.categories, TABS_LABELS.categories, routes.trendingTags()),
        icon: isTabActive(TABS_URLS.categories) ? <CategoryIconActive /> : <CategoryIconInactive />
      }]),
    ];
  }, [currentUserName, isTabActive, onSendMetricAndCloseMenu, variant]);

  const isProfileTabActive = isTabActive(TABS_URLS.profile) || isProfileMenuOpen;

  return (
    <div className="ApplicationFooter">
      {footerTabs.map(tab => (tab.alwaysShow || tab.condition) && (
        <TabLink key={tab.type} tab={tab} isActive={!!isTabActive(tab.type)} />
      ))}
      <button
        className={clsx(
          "TabLink",
          "TabLink_isProfile",
          isProfileTabActive && "TabLink_isActive",
        )}
        onClick={onProfileClick}
        aria-label={`${currentUserName ?? "Log in"}`}
      >
        <div>{isProfileTabActive ? <ProfileIconActive /> : <ProfileIconInactive />}</div>
        <span>{ currentUserName ? "Profile" : "Log in" }</span>
      </button>
    </div>
  );
};
